/**
 * currency.js — Shared Saudi Riyal formatting utilities
 *
 * Rules (per Saudi Central Bank / SAMA guidance):
 *  - The Riyal symbol (﷼) ALWAYS appears on the LEFT of the number
 *  - This applies in BOTH Arabic (RTL) and English (LTR) layouts
 *  - Numbers use comma-separated thousands: 1,125 not 1125
 *  - Decimal places are configurable (default 2)
 *
 * Usage:
 *   import { formatSAR, SARAmount } from "../../utils/currency";
 *
 *   // Plain string (for dialog messages, placeholders, etc.)
 *   formatSAR(1125)          → "﷼ 1,125.00"
 *   formatSAR(1125, 0)       → "﷼ 1,125"
 *
 *   // React component (renders SVG symbol + formatted number)
 *   <SARAmount value={1125} />
 *   <SARAmount value={1125} decimals={0} />
 */

import React from "react";

/**
 * Format a number as a SAR string with the symbol on the LEFT.
 * @param {number|string} value
 * @param {number} decimals  — decimal places (default 2)
 * @returns {string}  e.g. "﷼ 1,125.00"
 */
export function formatSAR(value, decimals = 2) {
  const num = parseFloat(value);
  if (isNaN(num)) return "﷼ —";
  const formatted = num.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  return `\uFDFC\u00A0${formatted}`;
}

/**
 * React component: renders the SVG Riyal symbol on the LEFT + comma-formatted number.
 * Decimal part is rendered slightly smaller for visual hierarchy.
 *
 * Props:
 *   value       {number|string}  The amount
 *   decimals    {number}         Decimal places (default 2)
 *   decimalSize {string}         Font size for decimal part (default "0.72em")
 *   style       {object}         Optional outer span style
 *   className   {string}         Optional outer span className
 */
export function SARAmount({ value, decimals = 2, decimalSize = "0.72em", style, className }) {
  const num = parseFloat(value);

  const imgStyle = {
    height: "1.05em",
    width: "auto",
    display: "inline-block",
    verticalAlign: "middle",
    marginRight: "3px",
    marginBottom: "1px",
    flexShrink: 0,
  };

  if (isNaN(num)) {
    return (
      <span className={className} style={{ display: "inline-flex", alignItems: "center", direction: "ltr", ...style }}>
        <img src="/img/riyal_symbol.svg" alt="SAR" style={imgStyle} />
        <span>—</span>
      </span>
    );
  }

  const formatted = num.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  const dotIndex = formatted.lastIndexOf(".");
  const intPart = dotIndex === -1 || decimals === 0 ? formatted : formatted.slice(0, dotIndex);
  const decPart = dotIndex === -1 || decimals === 0 ? "" : formatted.slice(dotIndex);

  return (
    <span
      className={className}
      style={{ display: "inline-flex", alignItems: "center", direction: "ltr", whiteSpace: "nowrap", ...style }}
    >
      {/* Symbol always on the LEFT — direction:ltr ensures this even inside RTL containers */}
      <img src="/img/riyal_symbol.svg" alt="SAR" style={imgStyle} />
      <span>
        {intPart}
        {decPart && (
          <span style={{ fontSize: decimalSize, opacity: 0.85 }}>{decPart}</span>
        )}
      </span>
    </span>
  );
}

export default SARAmount;
